/**
 * JobCreateSheet — Full Create Job form rendered as a Sheet overlay.
 * Mirrors the exact same form from Schedule.jsx (List View).
 * Self-contained: fetches its own reference data, handles submission independently.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { getApiErrorMessage } from '../utils/apiErrors';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '../components/ui/sheet';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { DatePicker } from '../components/ui/date-picker';
import { GooglePlacesAutocomplete } from '../components/ui/google-places-autocomplete';
import QuickAddCustomerDialog from '../components/QuickAddCustomerDialog';
import HaulageStopsEditor from '../components/HaulageStopsEditor';
import useLocale from '../hooks/useLocale';
import { getStatesForCountry, getCountryByCode } from '../utils/countryStates';
import {
  Loader2,
  Save,
  Check,
  ChevronDown,
  MapPin,
  Truck,
  Users,
  Plus,
  X,
  XCircle,
  Upload,
  Eye,
  EyeOff,
  File as FileIcon,
  User,
  Phone,
  Mail,
  Wrench,
  Moon,
  Navigation,
  Repeat,
} from 'lucide-react';
import RecurrenceConfig from '../components/RecurrenceConfig';
import AssetRequirementsEditor from '../components/AssetRequirementsEditor';
import { useFeatureFlags } from '../context/FeatureFlagsContext';

const HOURS_24 = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'));
const MINUTES_5 = ['00', '05', '10', '15', '20', '25', '30', '35', '40', '45', '50', '55'];
const DURATION_OPTIONS = [0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6, 6.5, 7, 7.5, 8, 8.5, 9, 9.5, 10, 10.5, 11, 11.5, 12];

const INITIAL_FORM = {
  customer_id: '', contact_id: '', po_number: '',
  site_id: '', site_contact_ids: [],
  use_one_off_address: false, one_off_street: '', one_off_suburb: '', one_off_state: '', one_off_postcode: '',
  requires_multi_stops: false,
  latitude: null, longitude: null,
  equipment_type: '', create_equipment_id: '', attachment_type_ids: [],
  worker_requirements: [],
  start_date: '', end_date: '',
  depot_time: '06:00', start_time: '07:00', est_duration: '', end_time: '',
  depot_id: '',
  description: '', notes: '', tags: [],
  is_night_shift: false, is_tbc: false,
  starting_location: '',
  // Phase 6: hire shape discriminator. 'wet' | 'dry' | 'labour'
  hire_type: 'wet',
  hire_period_unit: '',
  // Haulage fields
  job_type: 'standard',
  stops: [],
  // Multi-asset (gated by multi_asset_jobs flag)
  asset_requirements: [{ equipment_type: '', quantity: 1 }],
};

export default function JobCreateSheet({ open, onClose, onCreated, api, prefill, tenantSettings }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ ...INITIAL_FORM });
  const { isEnabled: isFeatureEnabled } = useFeatureFlags();
  const multiAssetEnabled = isFeatureEnabled('multi_asset_jobs');

  // Locale & country
  const t = useLocale(tenantSettings?.locale);
  const statesList = getStatesForCountry(tenantSettings?.country);
  const countryMeta = getCountryByCode(tenantSettings?.country);

  // PO required tracking
  const [selectedCustomerRequiresPO, setSelectedCustomerRequiresPO] = useState(false);

  // Reference data (self-fetched)
  const [customers, setCustomers] = useState([]);
  const [sites, setSites] = useState([]);
  const [equipmentTypes, setEquipmentTypes] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [workerTypes, setWorkerTypes] = useState([]);
  const [customerContacts, setCustomerContacts] = useState([]);
  const [siteContacts, setSiteContacts] = useState([]);
  const [attachmentTypes, setAttachmentTypes] = useState([]);
  const [depots, setDepots] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [pendingFiles, setPendingFiles] = useState([]);
  const [pendingFilesVisible, setPendingFilesVisible] = useState(false);
  const pendingFileInputRef = useRef(null);

  // Combobox open states
  const [customerOpen, setCustomerOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [siteOpen, setSiteOpen] = useState(false);
  const [assetTypeOpen, setAssetTypeOpen] = useState(false);
  const [assetOpen, setAssetOpen] = useState(false);
  const [siteContactOpen, setSiteContactOpen] = useState(false);

  // Recurring job state
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurrenceConfig, setRecurrenceConfig] = useState(null);

  // Quick-add dialog states
  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [contactForm, setContactForm] = useState({ name: '', phone: '', email: '', position: '' });
  const [siteDialogOpen, setSiteDialogOpen] = useState(false);
  const [siteDialogStopIdx, setSiteDialogStopIdx] = useState(null);
  const [siteForm, setSiteForm] = useState({ name: '', street_address: '', suburb: '', state: '', postcode: '', notes: '' });

  // Fetch reference data on open
  useEffect(() => {
    if (!open) return;
    const fetchRefs = async () => {
      try {
        const [custRes, siteRes, eqRes, eqTypesRes, wtRes, atRes, tagRes, depotRes] = await Promise.all([
          api.get('/customers'),
          api.get('/sites'),
          api.get('/equipment'),
          api.get('/equipment-types').catch(() => ({ data: [] })),
          api.get('/worker-types').catch(() => ({ data: [] })),
          api.get('/data-management/attachment-types').catch(() => ({ data: [] })),
          api.get('/jobs/tags').catch(() => ({ data: [] })),
          tenantSettings?.depots_enabled ? api.get('/depots?active_only=true').catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
        ]);
        setCustomers(custRes.data || []);
        setSites(siteRes.data || []);
        setEquipment(eqRes.data || []);
        setDepots(depotRes.data || []);
        // Use equipment_types endpoint (same source as Schedule page) for the full list
        const typeNames = (eqTypesRes.data || []).filter(Boolean).sort();
        setEquipmentTypes(typeNames.length > 0
          ? typeNames
          : [...new Set((eqRes.data || []).map(e => e.equipment_type).filter(Boolean))].sort()
        );
        setWorkerTypes(wtRes.data || []);
        setAttachmentTypes(atRes.data || []);
        setAvailableTags(tagRes.data || []);
      } catch (e) { console.error('Failed to fetch refs:', e); }
    };
    fetchRefs();
  }, [open, api]);

  // Reset + apply prefill on open
  useEffect(() => {
    if (!open) return;
    const newForm = { ...INITIAL_FORM };
    if (prefill?.date) newForm.start_date = prefill.date;
    if (prefill?.equipmentType) newForm.equipment_type = prefill.equipmentType;
    if (prefill?.equipmentId) newForm.create_equipment_id = prefill.equipmentId;
    // Auto-set job_type for haulage tenants
    if (tenantSettings?.industry === 'haulage') newForm.job_type = 'haulage';
    setForm(newForm);
    setPendingFiles([]);
    setTagInput('');
    setCustomerContacts([]);
    setSiteContacts([]);
    setSelectedCustomerRequiresPO(false);
  }, [open, prefill, tenantSettings]);

  // Helper: format site address
  const formatSiteAddress = useCallback((site) => {
    if (!site) return '';
    if (site.street_address || site.suburb) {
      return [site.street_address, site.suburb, site.state, site.postcode].filter(Boolean).join(', ');
    }
    return site.address || '';
  }, []);

  // Helper: get customer sites
  const getCustomerSites = useCallback((customerId) => sites.filter(s => s.customer_id === customerId), [sites]);

  // Fetch customer contacts
  const fetchCustomerContacts = useCallback(async (customerId) => {
    if (!customerId) { setCustomerContacts([]); return; }
    try {
      const res = await api.get(`/customers/${customerId}/contacts`);
      setCustomerContacts(res.data || []);
    } catch { setCustomerContacts([]); }
  }, [api]);

  // Fetch site contacts
  const fetchSiteContacts = useCallback(async (siteId) => {
    if (!siteId) { setSiteContacts([]); return; }
    try {
      const res = await api.get(`/sites/${siteId}/contacts`);
      setSiteContacts(res.data || []);
    } catch { setSiteContacts([]); }
  }, [api]);

  const filteredAttachments = attachmentTypes.filter(at =>
    !at.equipment_types?.length || at.equipment_types.includes(form.equipment_type)
  );

  const updateForm = useCallback((field, value) => {
    setForm(prev => {
      const next = { ...prev, [field]: value };
      if (field === 'customer_id') { next.site_id = ''; next.contact_id = ''; next.site_contact_ids = []; }
      if (field === 'site_id') { next.site_contact_ids = []; }
      if (field === 'equipment_type') { next.create_equipment_id = ''; next.attachment_type_ids = []; }
      // Auto-calc end_time from start_time + duration
      if ((field === 'est_duration' || field === 'start_time') && next.start_time && next.est_duration) {
        const [h, m] = next.start_time.split(':').map(Number);
        const dur = parseFloat(next.est_duration);
        const dH = Math.floor(dur); const dM = (dur % 1) * 60;
        let eH = h + dH; let eM = m + dM;
        if (eM >= 60) { eH += 1; eM -= 60; }
        if (eH >= 24) eH -= 24;
        next.end_time = `${eH.toString().padStart(2, '0')}:${Math.round(eM).toString().padStart(2, '0')}`;
      }
      return next;
    });
  }, []);

  // Customer selection handler
  const handleCustomerSelect = useCallback((value) => {
    if (value === '__add_new__') {
      setCustomerDialogOpen(true);
    } else {
      const customer = customers.find(c => c.id === value);
      setSelectedCustomerRequiresPO(customer?.requires_po || false);
      setForm(prev => ({ ...prev, customer_id: value, contact_id: '', site_id: '', site_contact_ids: [] }));
      fetchCustomerContacts(value);
      setSiteContacts([]);
    }
  }, [customers, fetchCustomerContacts]);

  // Contact selection handler
  const handleContactSelect = useCallback((value) => {
    if (value === '__add_new__') {
      setContactForm({ name: '', phone: '', email: '', position: '' });
      setContactDialogOpen(true);
    } else {
      setForm(prev => ({ ...prev, contact_id: value }));
    }
  }, []);

  // Site selection handler
  const handleSiteSelect = useCallback((value) => {
    if (value === '__add_new__') {
      setSiteForm({ name: '', street_address: '', suburb: '', state: '', postcode: '', notes: '' });
      setSiteDialogOpen(true);
    } else {
      setForm(prev => ({ ...prev, site_id: value, site_contact_ids: [] }));
      fetchSiteContacts(value);
    }
  }, [fetchSiteContacts]);

  // Quick-add handlers
  const handleCustomerCreated = async (newCustomer) => {
    const custRes = await api.get('/customers');
    setCustomers(custRes.data);
    setForm(prev => ({ ...prev, customer_id: newCustomer.id, contact_id: '', site_id: '' }));
    setCustomerContacts([]);
    setSelectedCustomerRequiresPO(newCustomer?.requires_po || false);
  };

  const handleCreateContact = async (e) => {
    e.preventDefault();
    try {
      const contactData = { ...contactForm, customer_id: form.customer_id };
      const response = await api.post(`/customers/${form.customer_id}/contacts`, contactData);
      const newContact = response.data;
      toast.success('Contact created');
      setContactDialogOpen(false);
      await fetchCustomerContacts(form.customer_id);
      setForm(prev => ({ ...prev, contact_id: newContact.id }));
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to create contact'));
    }
  };

  const handleCreateSite = async (e) => {
    e.preventDefault();
    try {
      const response = await api.post('/sites', { ...siteForm, customer_id: form.customer_id });
      const newSite = response.data;
      toast.success('Site created');
      setSiteDialogOpen(false);
      setSiteForm({ name: '', street_address: '', suburb: '', state: '', postcode: '', notes: '' });
      const sitesRes = await api.get('/sites');
      setSites(sitesRes.data);

      if (siteDialogStopIdx !== null) {
        setForm(prev => {
          const stops = [...(prev.stops || [])];
          if (stops[siteDialogStopIdx]) {
            stops[siteDialogStopIdx] = {
              ...stops[siteDialogStopIdx],
              site_id: newSite.id,
              address: newSite.street_address || '',
              suburb: newSite.suburb || '',
              state: newSite.state || '',
              postcode: newSite.postcode || '',
            };
          }
          return { ...prev, stops };
        });
        setSiteDialogStopIdx(null);
      } else {
        setForm(prev => ({ ...prev, site_id: newSite.id }));
      }
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to create site'));
    }
  };

  const handleAddTag = () => {
    if (!tagInput.trim()) return;
    if (!form.tags.includes(tagInput.trim())) {
      setForm(prev => ({ ...prev, tags: [...prev.tags, tagInput.trim()] }));
    }
    setTagInput('');
  };

  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return '';
    try { return format(new Date(dateStr + 'T00:00'), 'dd MMM yyyy'); } catch { return dateStr; }
  };

  const handleSubmit = async () => {
    if (!form.customer_id) { toast.error('Customer is required'); return; }
    const isLabour = form.hire_type === 'labour' || form.equipment_type === 'No Asset';
    // Multi-asset validation
    if (multiAssetEnabled && !isLabour) {
      const validReqs = form.asset_requirements.filter(r => r.equipment_type);
      if (validReqs.length === 0) { toast.error('At least one asset type is required'); return; }
    } else if (!isLabour && !form.equipment_type) {
      toast.error('Asset Type is required'); return;
    }
    if (isLabour && (!form.worker_requirements || form.worker_requirements.length === 0)) { toast.error('At least one worker type is required for labour-hire jobs'); return; }
    if (!form.start_date) { toast.error('Start Date is required'); return; }
    if (!form.start_time) { toast.error('Start Time is required'); return; }
    if (!form.description?.trim()) { toast.error('Description is required'); return; }
    if (!form.use_one_off_address && !form.site_id && form.job_type !== 'haulage' && !form.requires_multi_stops) { toast.error('Location is required'); return; }
    if ((form.job_type === 'haulage' || form.requires_multi_stops) && (!form.stops || form.stops.length < 2)) { toast.error('Multi-stop jobs require at least 2 stops (pickup + dropoff)'); return; }
    if (form.use_one_off_address && !form.one_off_street?.trim()) { toast.error('Street address is required'); return; }
    if (selectedCustomerRequiresPO && !form.po_number?.trim()) { toast.error('PO Number is required for this customer'); return; }
    const _slRule = tenantSettings?.field_rules?.starting_location;
    if (_slRule?.enabled && _slRule?.required && !form.starting_location?.trim()) { toast.error('Starting Location is required'); return; }

    setSaving(true);
    try {
      const submitData = { ...form, status: 'not_allocated' };

      // Multi-asset: set asset_requirements and sync root equipment_type
      if (multiAssetEnabled && !isLabour) {
        const validReqs = form.asset_requirements.filter(r => r.equipment_type);
        if (validReqs.length > 0) {
          submitData.asset_requirements = validReqs;
          submitData.equipment_type = validReqs[0].equipment_type;
        } else {
          submitData.asset_requirements = null;
        }
      } else {
        // Flag off or labour: strip asset_requirements
        delete submitData.asset_requirements;
      }
      if (!submitData.end_date) submitData.end_date = null;
      if (!submitData.est_duration && submitData.est_duration !== 0) submitData.est_duration = null;
      else submitData.est_duration = parseFloat(submitData.est_duration);
      if (!submitData.end_time) submitData.end_time = null;
      const createEquipmentId = submitData.create_equipment_id;
      delete submitData.create_equipment_id;
      if (submitData.job_type === 'haulage') {
        // For haulage, location is embedded in stops; clear site fields
        submitData.site_id = null;
        submitData.one_off_street = null; submitData.one_off_suburb = null;
        submitData.one_off_state = null; submitData.one_off_postcode = null;
        // Generate route_summary
        const locations = (submitData.stops || [])
          .sort((a, b) => a.order - b.order)
          .map(s => s.suburb || s.address || `Stop ${s.order}`)
          .filter((v, i, a) => !i || a[i - 1] !== v);
        submitData.route_summary = locations.join(' → ');
      } else if (submitData.use_one_off_address) { submitData.site_id = null; }
      else { submitData.one_off_street = null; submitData.one_off_suburb = null; submitData.one_off_state = null; submitData.one_off_postcode = null; }
      delete submitData.use_one_off_address;
      // Site contacts only apply to a single selected site
      if (submitData.job_type === 'haulage' || submitData.requires_multi_stops || !submitData.site_id) submitData.site_contact_ids = [];

      // Dry-hire field cleanup (match Schedule.jsx behaviour)
      if (submitData.hire_type !== 'dry') {
        submitData.hire_period_unit = null;
        submitData.pickup_from_yard = false;
        submitData.return_to_yard = false;
        submitData.hire_notes = null;
        submitData.expected_off_hire_date = null;
      } else {
        if (!submitData.hire_period_unit) submitData.hire_period_unit = 'day';
        if (submitData.end_date === '') submitData.end_date = null;
        if (submitData.expected_off_hire_date === '') submitData.expected_off_hire_date = null;
      }

      // ── Submit: recurring or single ──────────────────────────────
      if (isRecurring && recurrenceConfig) {
        const res = await api.post('/jobs/recurring', {
          job: submitData,
          recurrence: recurrenceConfig,
        });
        const count = res.data?.total_created || 0;
        toast.success(`${count} recurring job${count !== 1 ? 's' : ''} created`);
        // No auto-allocation or file upload for recurring (batch)
        onCreated?.();
        onClose();
        return;
      }

      const response = await api.post('/jobs', submitData);
      const newJobId = response.data?.id;
      toast.success(`Job #${response.data?.job_number} created`);

      // Auto-allocate asset
      if (newJobId && (createEquipmentId || prefill?.equipmentId)) {
        const eqId = createEquipmentId || prefill?.equipmentId;
        try {
          await api.post('/allocations?skip_validation=true', {
            job_id: newJobId, equipment_id: eqId, operator_id: '',
            start_date: submitData.start_date, end_date: submitData.end_date || submitData.start_date, status: 'draft'
          });
          // Backend recalculates job.status inside POST /allocations — no follow-up PUT needed.
          toast.success('Asset allocated');
        } catch (e) { console.warn('Auto-allocation failed:', e); }
      }

      // Upload files
      if (newJobId && pendingFiles.length > 0) {
        let ok = 0;
        for (const pf of pendingFiles) {
          const fd = new FormData();
          fd.append('file', pf.file);
          fd.append('visible_to_operators', pf.visibleToOperators);
          try { await api.post(`/jobs/${newJobId}/files`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); ok++; } catch { toast.error(`Failed: ${pf.file.name}`); }
        }
        if (ok > 0) toast.success(`${ok} file${ok > 1 ? 's' : ''} attached`);
      }

      onCreated?.();
      onClose();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to create job'));
    } finally {
      setSaving(false);
    }
  };

  const sortedCustomers = [...customers].sort((a, b) => (a.company_name || '').localeCompare(b.company_name || ''));
  const customerSites = getCustomerSites(form.customer_id);

  return (
    <Sheet open={open} onOpenChange={(v) => {
      // Ignore close requests while a nested quick-add dialog is open so
      // dismissing the inner dialog doesn't also close this job form.
      if (!v && (contactDialogOpen || siteDialogOpen || customerDialogOpen)) {
        return;
      }
      if (!v) onClose();
    }}>
      <SheetContent side="right" className="w-[800px] sm:max-w-[800px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Create New Job</SheetTitle>
        </SheetHeader>

        <div className="space-y-5 py-6">
          {/* Customer */}
          <div className="space-y-2">
            <Label className="label-overline">Customer <span className="text-red-500">*</span></Label>
            <Popover open={customerOpen} onOpenChange={setCustomerOpen} modal>
              <PopoverTrigger asChild>
                <Button variant="outline" role="combobox" aria-expanded={customerOpen} className="w-full justify-between font-normal" data-testid="pb-job-customer-select">
                  {form.customer_id ? (
                    <span className="flex items-center gap-2 truncate">
                      {sortedCustomers.find(c => c.id === form.customer_id)?.company_name || 'Select customer'}
                      {sortedCustomers.find(c => c.id === form.customer_id)?.requires_po && (
                        <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700">PO</Badge>
                      )}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">Search customers...</span>
                  )}
                  <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Search customers..." />
                  <CommandList className="max-h-[450px] overflow-y-auto">
                    <CommandEmpty>No customer found.</CommandEmpty>
                    <CommandGroup>
                      <CommandItem value="__add_new__" onSelect={() => { handleCustomerSelect('__add_new__'); setCustomerOpen(false); }} className="text-[#ffd108] font-medium">
                        <Plus className="mr-2 h-4 w-4" />
                        ADD NEW CUSTOMER
                      </CommandItem>
                    </CommandGroup>
                    {sortedCustomers.length > 0 && (
                      <CommandGroup heading="Customers">
                        {sortedCustomers.map(c => (
                          <CommandItem key={c.id} value={c.company_name} onSelect={() => { handleCustomerSelect(c.id); setCustomerOpen(false); }}>
                            <Check className={`mr-2 h-4 w-4 ${form.customer_id === c.id ? 'opacity-100' : 'opacity-0'}`} />
                            <span className="flex items-center gap-2">
                              {c.company_name}
                              {c.requires_po && (
                                <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700 ml-1">PO</Badge>
                              )}
                            </span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    )}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          {/* Contact */}
          {form.customer_id && (
            <div className="space-y-2">
              <Label className="label-overline">Contact</Label>
              <Popover open={contactOpen} onOpenChange={setContactOpen} modal>
                <PopoverTrigger asChild>
                  <Button variant="outline" role="combobox" aria-expanded={contactOpen} className="w-full justify-between font-normal" data-testid="pb-job-contact-select">
                    {form.contact_id ? (
                      <span className="flex items-center gap-2 truncate">
                        <User className="h-3 w-3 text-muted-foreground" />
                        {customerContacts.find(c => c.id === form.contact_id)?.name || 'Select contact'}
                        {customerContacts.find(c => c.id === form.contact_id)?.position && (
                          <span className="text-muted-foreground text-xs">({customerContacts.find(c => c.id === form.contact_id)?.position})</span>
                        )}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">Select contact (optional)</span>
                    )}
                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search contacts..." />
                    <CommandList className="max-h-[300px] overflow-y-auto">
                      <CommandEmpty>No contact found.</CommandEmpty>
                      <CommandGroup>
                        <CommandItem value="__add_new_contact__" onSelect={() => { handleContactSelect('__add_new__'); setContactOpen(false); }} className="text-[#ffd108] font-medium">
                          <Plus className="mr-2 h-4 w-4" />
                          ADD NEW CONTACT
                        </CommandItem>
                      </CommandGroup>
                      {customerContacts.length > 0 && (
                        <CommandGroup heading="Contacts">
                          {customerContacts.map(contact => (
                            <CommandItem key={contact.id} value={contact.name} onSelect={() => { handleContactSelect(contact.id); setContactOpen(false); }}>
                              <Check className={`mr-2 h-4 w-4 ${form.contact_id === contact.id ? 'opacity-100' : 'opacity-0'}`} />
                              <span className="flex items-center gap-2">
                                {contact.name}
                                {contact.position && <span className="text-muted-foreground text-xs">({contact.position})</span>}
                              </span>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      )}
                      {customerContacts.length === 0 && (
                        <div className="px-2 py-3 text-sm text-muted-foreground text-center">
                          No contacts for this customer
                        </div>
                      )}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          )}

          {/* PO Number - Required if customer.requires_po is true */}
          {selectedCustomerRequiresPO && (
            <div className="space-y-2">
              <Label className="label-overline flex items-center gap-2">
                PO Number
                <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700">Required</Badge>
              </Label>
              <Input
                value={form.po_number}
                onChange={(e) => updateForm('po_number', e.target.value)}
                placeholder="Enter Purchase Order number"
                required
                data-testid="pb-job-po-number"
              />
            </div>
          )}

          {/* Optional PO Number for non-required customers */}
          {!selectedCustomerRequiresPO && form.customer_id && (
            <div className="space-y-2">
              <Label className="label-overline">PO Number (Optional)</Label>
              <Input
                value={form.po_number}
                onChange={(e) => updateForm('po_number', e.target.value)}
                placeholder="Enter Purchase Order number if applicable"
                data-testid="pb-job-po-number"
              />
            </div>
          )}

          {/* Location — Haulage/Multi-Stop: Stops Editor | Standard: Site picker */}
          {(form.job_type === 'haulage' || form.requires_multi_stops) ? (
            <div className="space-y-2">
              <Label className="label-overline flex items-center gap-2">
                <MapPin className="h-4 w-4" /> Route Stops
              </Label>
              <HaulageStopsEditor
                stops={form.stops || []}
                onChange={(stops) => setForm(prev => ({ ...prev, stops }))}
                country={tenantSettings?.country || 'AU'}
                sites={form.customer_id ? getCustomerSites(form.customer_id) : []}
                onAddNewSite={(stopIdx) => { setSiteDialogStopIdx(stopIdx); setSiteDialogOpen(true); }}
                api={api}
              />
            </div>
          ) : (
          <>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="label-overline">Job Location <span className="text-red-500">*</span></Label>
              <div className="flex items-center gap-2">
                <Label className="text-xs text-muted-foreground">One-off address</Label>
                <Switch
                  checked={form.use_one_off_address}
                  onCheckedChange={(checked) => setForm(prev => ({
                    ...prev,
                    use_one_off_address: checked,
                    site_id: checked ? '' : prev.site_id,
                    site_contact_ids: checked ? [] : prev.site_contact_ids,
                    one_off_street: checked ? prev.one_off_street : '',
                    one_off_suburb: checked ? prev.one_off_suburb : '',
                    one_off_state: checked ? prev.one_off_state : '',
                    one_off_postcode: checked ? prev.one_off_postcode : ''
                  }))}
                />
              </div>
            </div>

            {form.use_one_off_address ? (
              <div className="space-y-3 p-3 border rounded-lg bg-muted/30">
                <div>
                  <Label className="text-xs text-muted-foreground">Search Address</Label>
                  <GooglePlacesAutocomplete
                    placeholder="Start typing an address..."
                    country={tenantSettings?.country}
                    onPlaceSelect={(place) => {
                      setForm(prev => ({
                        ...prev,
                        one_off_street: place.street_address || '',
                        one_off_suburb: place.suburb || '',
                        one_off_state: place.state || '',
                        one_off_postcode: place.postcode || ''
                      }));
                    }}
                    data-testid="pb-job-one-off-address-autocomplete"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Or enter manually below</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Street Address</Label>
                  <Input
                    value={form.one_off_street}
                    onChange={(e) => updateForm('one_off_street', e.target.value)}
                    placeholder="e.g., 123 Main Street"
                    data-testid="pb-job-one-off-street"
                  />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label className="text-xs text-muted-foreground">{t('suburb')}</Label>
                    <Input
                      value={form.one_off_suburb}
                      onChange={(e) => updateForm('one_off_suburb', e.target.value)}
                      placeholder={t('suburb')}
                      data-testid="pb-job-one-off-suburb"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">{countryMeta?.stateLabel || t('state')}</Label>
                    <Select
                      value={form.one_off_state}
                      onValueChange={(v) => updateForm('one_off_state', v)}
                    >
                      <SelectTrigger data-testid="pb-job-one-off-state">
                        <SelectValue placeholder={countryMeta?.stateLabel || t('state')} />
                      </SelectTrigger>
                      <SelectContent>
                        {statesList.map(s => (
                          <SelectItem key={s.code} value={s.code}>{s.code}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">{countryMeta?.postalLabel || t('postcode')}</Label>
                    <Input
                      value={form.one_off_postcode}
                      onChange={(e) => updateForm('one_off_postcode', e.target.value)}
                      placeholder="0000"
                      maxLength={4}
                      data-testid="pb-job-one-off-postcode"
                    />
                  </div>
                </div>
              </div>
            ) : (
              <Popover open={siteOpen} onOpenChange={setSiteOpen} modal>
                <PopoverTrigger asChild>
                  <Button variant="outline" role="combobox" aria-expanded={siteOpen} className="w-full justify-between font-normal" disabled={!form.customer_id} data-testid="pb-job-site-select">
                    {form.site_id ? (
                      <span className="truncate">
                        {(() => {
                          const s = customerSites.find(s => s.id === form.site_id);
                          return s ? `${s.name ? s.name + ' - ' : ''}${formatSiteAddress(s)}`.replace(/ - $/, '') || 'Unnamed Site' : 'Select site';
                        })()}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">{form.customer_id ? 'Search sites...' : 'Select customer first'}</span>
                    )}
                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search sites..." />
                    <CommandList className="max-h-[300px] overflow-y-auto">
                      <CommandEmpty>No site found.</CommandEmpty>
                      {form.customer_id && (
                        <CommandGroup>
                          <CommandItem value="__add_new_site__" onSelect={() => { handleSiteSelect('__add_new__'); setSiteOpen(false); }} className="text-[#ffd108] font-medium">
                            <Plus className="mr-2 h-4 w-4" />
                            ADD NEW SITE
                          </CommandItem>
                        </CommandGroup>
                      )}
                      {customerSites.length > 0 && (
                        <CommandGroup heading="Sites">
                          {customerSites.map(s => (
                            <CommandItem key={s.id} value={`${s.name || ''} ${formatSiteAddress(s)}`} onSelect={() => { handleSiteSelect(s.id); setSiteOpen(false); }}>
                              <Check className={`mr-2 h-4 w-4 ${form.site_id === s.id ? 'opacity-100' : 'opacity-0'}`} />
                              {s.name ? `${s.name} - ${formatSiteAddress(s)}` : formatSiteAddress(s) || 'Unnamed Site'}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      )}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            )}
          </div>

          {/* Site Contacts */}
          {form.site_id && !form.use_one_off_address && (
            <div className="space-y-2" data-testid="pb-site-contact-section">
              <Label className="label-overline flex items-center gap-2">
                <User className="h-4 w-4" />
                Site Contacts
              </Label>
              <Popover open={siteContactOpen} onOpenChange={setSiteContactOpen} modal>
                <PopoverTrigger asChild>
                  <Button variant="outline" role="combobox" aria-expanded={siteContactOpen} className="w-full justify-between font-normal" data-testid="pb-job-site-contact-select">
                    {form.site_contact_ids?.length > 0 ? (
                      <span className="truncate">{form.site_contact_ids.length} contact{form.site_contact_ids.length > 1 ? 's' : ''} selected</span>
                    ) : (
                      <span className="text-muted-foreground">Select site contacts...</span>
                    )}
                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search site contacts..." />
                    <CommandList className="max-h-[300px] overflow-y-auto">
                      <CommandEmpty>No site contact found.</CommandEmpty>
                      <CommandGroup heading="Site Contacts">
                        {siteContacts.map(contact => {
                          const selected = form.site_contact_ids?.includes(contact.id);
                          return (
                            <CommandItem
                              key={contact.id}
                              value={contact.name || `site-contact-${contact.id}`}
                              onSelect={() => {
                                setForm(prev => {
                                  const ids = prev.site_contact_ids || [];
                                  return {
                                    ...prev,
                                    site_contact_ids: ids.includes(contact.id)
                                      ? ids.filter(id => id !== contact.id)
                                      : [...ids, contact.id],
                                  };
                                });
                              }}
                              data-testid={`pb-site-contact-option-${contact.id}`}
                            >
                              <Check className={`mr-2 h-4 w-4 ${selected ? 'opacity-100' : 'opacity-0'}`} />
                              <span className="flex items-center gap-2">
                                {contact.name}
                                {contact.job_title && <span className="text-muted-foreground text-xs">({contact.job_title})</span>}
                                {contact.is_primary && <Badge variant="outline" className="text-xs ml-1">Primary</Badge>}
                              </span>
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {form.site_contact_ids?.length > 0 && (
                <div className="space-y-1.5" data-testid="pb-selected-site-contacts">
                  {form.site_contact_ids.map(id => {
                    const contact = siteContacts.find(c => c.id === id);
                    if (!contact) return null;
                    return (
                      <div key={id} className="flex items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm" data-testid={`pb-selected-site-contact-${id}`}>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 min-w-0">
                          <span className="font-medium truncate">{contact.name}</span>
                          {contact.is_primary && <Badge variant="outline" className="text-xs">Primary</Badge>}
                          {contact.phone && <span className="flex items-center gap-1 text-muted-foreground"><Phone className="h-3 w-3" />{contact.phone}</span>}
                          {contact.email && <span className="flex items-center gap-1 text-muted-foreground"><Mail className="h-3 w-3" />{contact.email}</span>}
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 shrink-0"
                          onClick={() => setForm(prev => ({ ...prev, site_contact_ids: (prev.site_contact_ids || []).filter(x => x !== id) }))}
                          data-testid={`pb-remove-site-contact-${id}`}
                        >
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          </>
          )}

          {/* Starting Location — gated on field_rules.starting_location.enabled */}
          {tenantSettings?.field_rules?.starting_location?.enabled && (
            <div className="space-y-2" data-testid="starting-location-section">
              <Label className="label-overline flex items-center gap-2">
                <Navigation className="h-4 w-4" />
                Starting Location {tenantSettings?.field_rules?.starting_location?.required && <span className="text-red-500">*</span>}
              </Label>
              <GooglePlacesAutocomplete
                placeholder="Where will workers start from?"
                country={tenantSettings?.country}
                value={form.starting_location}
                onChange={(e) => updateForm('starting_location', e.target.value)}
                onPlaceSelect={(place) => updateForm('starting_location', place.formatted_address || place.street_address || '')}
                data-testid="job-starting-location-input"
              />
            </div>
          )}

          {/* Multi-Stop Toggle — for non-haulage jobs when setting enabled */}
          {form.job_type !== 'haulage' && tenantSettings?.field_rules?.allow_multi_stops?.enabled && (
            <div className="flex items-center gap-2">
              <Switch
                id="pb-multi-stops"
                checked={form.requires_multi_stops}
                onCheckedChange={(checked) => setForm(prev => ({ ...prev, requires_multi_stops: checked, stops: checked ? (prev.stops?.length ? prev.stops : []) : prev.stops }))}
                data-testid="pb-multi-stops-toggle"
              />
              <Label htmlFor="pb-multi-stops" className="cursor-pointer text-sm flex items-center gap-1.5">
                <MapPin className="h-3.5 w-3.5" /> Requires Multi-Stops
              </Label>
            </div>
          )}

          {/* Hire Type */}
          <div className="space-y-2" data-testid="pb-hire-type-panel">
            <Label className="label-overline">Hire Type</Label>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                data-testid="pb-hire-type-wet-btn"
                onClick={() => { updateForm('hire_type', 'wet'); }}
                className={`flex-1 min-w-[120px] px-2 py-1.5 text-xs rounded-md border transition-colors ${
                  (form.hire_type || 'wet') === 'wet'
                    ? 'bg-primary text-primary-foreground border-primary font-semibold'
                    : 'bg-background hover:bg-accent border-border'
                }`}
              >
                Wet-Hire
              </button>
              <button
                type="button"
                data-testid="pb-hire-type-dry-btn"
                onClick={() => { updateForm('hire_type', 'dry'); updateForm('hire_period_unit', 'day'); }}
                className={`flex-1 min-w-[120px] px-2 py-1.5 text-xs rounded-md border transition-colors ${
                  form.hire_type === 'dry'
                    ? 'bg-amber-500 text-white border-amber-500 font-semibold'
                    : 'bg-background hover:bg-accent border-border'
                }`}
              >
                Dry-Hire
              </button>
              <button
                type="button"
                data-testid="pb-hire-type-labour-btn"
                onClick={() => {
                  updateForm('hire_type', 'labour');
                  updateForm('equipment_type', '');
                  updateForm('attachment_type_ids', []);
                  updateForm('create_equipment_id', '');
                }}
                className={`flex-1 min-w-[120px] px-2 py-1.5 text-xs rounded-md border transition-colors ${
                  form.hire_type === 'labour'
                    ? 'bg-indigo-500 text-white border-indigo-500 font-semibold'
                    : 'bg-background hover:bg-accent border-border'
                }`}
              >
                Labour Hire
              </button>
            </div>
            {form.hire_type === 'labour' && (
              <p className="text-xs text-muted-foreground">Workers only — no asset. Add at least one worker below.</p>
            )}
          </div>

          {/* Asset Type — hidden for labour hire */}
          {form.hire_type !== 'labour' && (
          multiAssetEnabled ? (
            <AssetRequirementsEditor
              requirements={form.asset_requirements}
              onChange={(reqs) => {
                setForm(prev => ({
                  ...prev,
                  asset_requirements: reqs,
                  // Sync root equipment_type for backward-compat display
                  equipment_type: reqs[0]?.equipment_type || prev.equipment_type,
                }));
              }}
              equipmentTypes={equipmentTypes}
            />
          ) : (
          <div className="space-y-2">
            <Label className="label-overline">Asset Type <span className="text-red-500">*</span></Label>
            <Popover open={assetTypeOpen} onOpenChange={setAssetTypeOpen} modal>
              <PopoverTrigger asChild>
                <Button variant="outline" role="combobox" aria-expanded={assetTypeOpen} className="w-full justify-between font-normal" data-testid="pb-job-asset-type-select">
                  {form.equipment_type ? <span className="truncate">{form.equipment_type}</span> : <span className="text-muted-foreground">Search asset types...</span>}
                  <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Search asset types..." />
                  <CommandList className="max-h-[300px] overflow-y-auto">
                    <CommandEmpty>No asset type found.</CommandEmpty>
                    <CommandGroup heading="Asset Types">
                      {equipmentTypes.map(t => (
                        <CommandItem key={t} value={t} onSelect={() => { updateForm('equipment_type', t); setAssetTypeOpen(false); }}>
                          <Check className={`mr-2 h-4 w-4 ${form.equipment_type === t ? 'opacity-100' : 'opacity-0'}`} />
                          {t}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
          )
          )}

          {/* Asset allocation on create - hidden for No Asset */}
          {form.equipment_type && form.equipment_type !== 'No Asset' && (prefill?.equipmentId || tenantSettings?.field_rules?.asset_allocation_on_create?.enabled) && (
            <div className="space-y-2">
              <Label className="label-overline flex items-center gap-2"><Truck className="h-4 w-4" />Asset</Label>
              <Popover open={assetOpen} onOpenChange={setAssetOpen} modal>
                <PopoverTrigger asChild>
                  <Button variant="outline" role="combobox" aria-expanded={assetOpen} className="w-full justify-between font-normal" data-testid="pb-job-create-asset-select">
                    {form.create_equipment_id ? (() => {
                      const eq = equipment.find(e => e.id === form.create_equipment_id);
                      return <span className="truncate">{eq ? `${eq.asset_id || eq.equipment_type}` : 'Unknown'}</span>;
                    })() : <span className="text-muted-foreground">Select an asset to allocate...</span>}
                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search assets..." />
                    <CommandList className="max-h-[300px] overflow-y-auto">
                      <CommandEmpty>No assets found for this type.</CommandEmpty>
                      <CommandGroup heading="Available Assets">
                        <CommandItem value="__none__" onSelect={() => { updateForm('create_equipment_id', ''); setAssetOpen(false); }}>
                          <Check className={`mr-2 h-4 w-4 ${!form.create_equipment_id ? 'opacity-100' : 'opacity-0'}`} />
                          <span className="text-muted-foreground">-- None --</span>
                        </CommandItem>
                        {equipment.filter(eq => eq.equipment_type === form.equipment_type && eq.status !== 'decommissioned').map(eq => (
                          <CommandItem key={eq.id} value={`${eq.asset_id || ''} ${eq.equipment_type || ''}`} onSelect={() => { updateForm('create_equipment_id', eq.id); setAssetOpen(false); }}>
                            <Check className={`mr-2 h-4 w-4 ${form.create_equipment_id === eq.id ? 'opacity-100' : 'opacity-0'}`} />
                            {eq.asset_id || eq.equipment_type}
                            {eq.status === 'maintenance' && <span className="ml-2 text-xs text-amber-500">(maintenance)</span>}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          )}

          {/* Attachment Types - hidden for No Asset */}
          {form.equipment_type && form.equipment_type !== 'No Asset' && filteredAttachments.length > 0 && (
            <div className="space-y-2">
              <Label className="label-overline flex items-center gap-2"><Wrench className="h-4 w-4" />Attachment Types Required</Label>
              <div className="border border-border rounded-md p-3 max-h-32 overflow-y-auto space-y-2">
                {filteredAttachments.map(at => (
                  <div key={at.id} className="flex items-center gap-2">
                    <Checkbox id={`pb-att-${at.id}`} checked={form.attachment_type_ids.includes(at.id)} onCheckedChange={(checked) => {
                      updateForm('attachment_type_ids', checked ? [...form.attachment_type_ids, at.id] : form.attachment_type_ids.filter(id => id !== at.id));
                    }} />
                    <label htmlFor={`pb-att-${at.id}`} className="text-sm cursor-pointer">{at.name}</label>
                  </div>
                ))}
              </div>
              {form.attachment_type_ids.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  {form.attachment_type_ids.length} type(s) selected
                </p>
              )}
            </div>
          )}

          {/* Worker Requirements — mandatory for labour hire, optional otherwise */}
          {(form.hire_type === 'labour' || form.equipment_type === 'No Asset' || (workerTypes.length > 0 && tenantSettings?.field_rules?.additional_workers?.enabled !== false)) && (
            <div className="space-y-2">
              <Label className="label-overline flex items-center gap-2">
                <Users className="h-4 w-4" />
                {(form.hire_type === 'labour' || form.equipment_type === 'No Asset') ? <>Workers Required <span className="text-red-500">*</span></> : 'Additional Workers Required'}
              </Label>
              <p className="text-xs text-muted-foreground mb-2">
                {(form.hire_type === 'labour' || form.equipment_type === 'No Asset')
                  ? 'At least one worker type is required for labour-hire jobs'
                  : 'Select workers like Dogmen, Spotters, Riggers, etc.'}
              </p>
              <div className="border border-border rounded-md p-3 space-y-3">
                {workerTypes.map(type => {
                  const existing = form.worker_requirements.find(wr => wr.worker_type_id === type.id);
                  return (
                    <div key={type.id} className="flex items-center gap-3">
                      <Checkbox id={`pb-wt-${type.id}`} checked={!!existing} onCheckedChange={(checked) => {
                        if (checked) updateForm('worker_requirements', [...form.worker_requirements, { worker_type_id: type.id, quantity: 1 }]);
                        else updateForm('worker_requirements', form.worker_requirements.filter(wr => wr.worker_type_id !== type.id));
                      }} />
                      <label htmlFor={`pb-wt-${type.id}`} className="text-sm cursor-pointer flex-1">
                        {type.name}
                        {type.description && <span className="text-xs text-muted-foreground ml-1">({type.description})</span>}
                      </label>
                      {existing && (
                        <div className="flex items-center gap-1">
                          <Button type="button" variant="outline" size="icon" className="h-6 w-6" disabled={existing.quantity <= 1}
                            onClick={() => updateForm('worker_requirements', form.worker_requirements.map(wr => wr.worker_type_id === type.id ? { ...wr, quantity: wr.quantity - 1 } : wr))}>-</Button>
                          <span className="text-sm font-medium w-6 text-center">{existing.quantity}</span>
                          <Button type="button" variant="outline" size="icon" className="h-6 w-6"
                            onClick={() => updateForm('worker_requirements', form.worker_requirements.map(wr => wr.worker_type_id === type.id ? { ...wr, quantity: wr.quantity + 1 } : wr))}>+</Button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {form.worker_requirements.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  {form.worker_requirements.reduce((sum, wr) => sum + wr.quantity, 0)} worker(s) selected: {
                    form.worker_requirements.map(wr => {
                      const wt = workerTypes.find(t => t.id === wr.worker_type_id);
                      return `${wr.quantity}x ${wt?.name || 'Unknown'}`;
                    }).join(', ')
                  }
                </p>
              )}
            </div>
          )}

          {/* Date fields */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="label-overline">Start Date <span className="text-red-500">*</span></Label>
                <DatePicker
                  value={form.start_date}
                  onChange={(d) => updateForm('start_date', d)}
                  formatDisplay={formatDateDisplay}
                  className="w-full"
                  testId="pb-job-start-date"
                />
              </div>
              <div className="space-y-2">
                <Label className="label-overline">End Date</Label>
                <DatePicker
                  value={form.end_date}
                  onChange={(d) => updateForm('end_date', d)}
                  formatDisplay={formatDateDisplay}
                  minDate={form.start_date}
                  className="w-full"
                  testId="pb-job-end-date"
                />
              </div>
            </div>
            <div className="space-y-4">
              {tenantSettings?.depot_time_enabled && (
              <div className="space-y-2">
                <Label className="label-overline">Depot Time</Label>
                <div className="flex gap-2">
                  <Select
                    value={form.depot_time?.split(':')[0] || '06'}
                    onValueChange={(hour) => {
                      const currentMinute = form.depot_time?.split(':')[1] || '00';
                      updateForm('depot_time', `${hour}:${currentMinute}`);
                    }}
                  >
                    <SelectTrigger className="w-20 font-mono"><SelectValue placeholder="HH" /></SelectTrigger>
                    <SelectContent>
                      {HOURS_24.map(hour => (
                        <SelectItem key={hour} value={hour}>{hour}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <span className="flex items-center text-lg font-bold">:</span>
                  <Select
                    value={form.depot_time?.split(':')[1] || '00'}
                    onValueChange={(minute) => {
                      const currentHour = form.depot_time?.split(':')[0] || '06';
                      updateForm('depot_time', `${currentHour}:${minute}`);
                    }}
                  >
                    <SelectTrigger className="w-20 font-mono"><SelectValue placeholder="MM" /></SelectTrigger>
                    <SelectContent>
                      {MINUTES_5.map(minute => (
                        <SelectItem key={minute} value={minute}>{minute}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              )}
              {tenantSettings?.depots_enabled && (
              <div className="space-y-2">
                <Label className="label-overline">Depot</Label>
                <Select
                  value={form.depot_id || '__none__'}
                  onValueChange={(value) => updateForm('depot_id', value === '__none__' ? '' : value)}
                >
                  <SelectTrigger data-testid="job-depot-select"><SelectValue placeholder="Select depot" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">No depot</SelectItem>
                    {depots.map(d => (
                      <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              )}
              <div className="space-y-2">
                <Label className="label-overline">Start Time <span className="text-red-500">*</span></Label>
                <div className="flex gap-2">
                  <Select
                    value={form.start_time?.split(':')[0] || '07'}
                    onValueChange={(hour) => {
                      const currentMinute = form.start_time?.split(':')[1] || '00';
                      const newStartTime = `${hour}:${currentMinute}`;
                      const updates = { start_time: newStartTime };
                      // Auto-calc end_time from start_time + duration
                      if (form.est_duration) {
                        const dur = parseFloat(form.est_duration);
                        const h = parseInt(hour);
                        const m = parseInt(currentMinute);
                        const dH = Math.floor(dur); const dM = (dur % 1) * 60;
                        let eH = h + dH; let eM = m + dM;
                        if (eM >= 60) { eH += 1; eM -= 60; }
                        if (eH >= 24) eH -= 24;
                        updates.end_time = `${eH.toString().padStart(2, '0')}:${Math.round(eM).toString().padStart(2, '0')}`;
                      }
                      setForm(p => ({ ...p, ...updates }));
                    }}
                  >
                    <SelectTrigger className="w-20 font-mono"><SelectValue placeholder="HH" /></SelectTrigger>
                    <SelectContent>
                      {HOURS_24.map(hour => (
                        <SelectItem key={hour} value={hour}>{hour}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <span className="flex items-center text-lg font-bold">:</span>
                  <Select
                    value={form.start_time?.split(':')[1] || '00'}
                    onValueChange={(minute) => {
                      const currentHour = form.start_time?.split(':')[0] || '07';
                      const newStartTime = `${currentHour}:${minute}`;
                      const updates = { start_time: newStartTime };
                      if (form.est_duration) {
                        const dur = parseFloat(form.est_duration);
                        const h = parseInt(currentHour);
                        const m = parseInt(minute);
                        const dH = Math.floor(dur); const dM = (dur % 1) * 60;
                        let eH = h + dH; let eM = m + dM;
                        if (eM >= 60) { eH += 1; eM -= 60; }
                        if (eH >= 24) eH -= 24;
                        updates.end_time = `${eH.toString().padStart(2, '0')}:${Math.round(eM).toString().padStart(2, '0')}`;
                      }
                      setForm(p => ({ ...p, ...updates }));
                    }}
                  >
                    <SelectTrigger className="w-20 font-mono"><SelectValue placeholder="MM" /></SelectTrigger>
                    <SelectContent>
                      {MINUTES_5.map(minute => (
                        <SelectItem key={minute} value={minute}>{minute}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Est. Duration</Label>
                <Select
                  value={form.est_duration?.toString() || 'none'}
                  onValueChange={(value) => {
                    const dur = value === 'none' ? '' : parseFloat(value);
                    const updates = { est_duration: dur };
                    if (dur && form.start_time) {
                      const [h, m] = form.start_time.split(':').map(Number);
                      const dH = Math.floor(dur);
                      const dM = (dur % 1) * 60;
                      let eH = h + dH;
                      let eM = m + dM;
                      if (eM >= 60) { eH += 1; eM -= 60; }
                      if (eH >= 24) eH -= 24;
                      updates.end_time = `${eH.toString().padStart(2, '0')}:${Math.round(eM).toString().padStart(2, '0')}`;
                    }
                    setForm(p => ({ ...p, ...updates }));
                  }}
                >
                  <SelectTrigger className="font-mono"><SelectValue placeholder="Select hours" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">-</SelectItem>
                    {DURATION_OPTIONS.map(h => <SelectItem key={h} value={h.toString()}>{h} {h === 1 ? 'hour' : 'hours'}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {/* End Time */}
              <div className="space-y-2">
                <Label className="label-overline">End Time</Label>
                <div className="flex items-center gap-1">
                  <Select
                    value={form.end_time ? form.end_time.split(':')[0] : 'none'}
                    onValueChange={(hour) => {
                      if (hour === 'none') {
                        setForm(p => ({ ...p, end_time: '', est_duration: '' }));
                        return;
                      }
                      const currentMin = form.end_time?.split(':')[1] || '00';
                      const newEnd = `${hour}:${currentMin}`;
                      const updates = { end_time: newEnd };
                      if (form.start_time) {
                        const [sH, sM] = form.start_time.split(':').map(Number);
                        const eH = parseInt(hour);
                        const eM = parseInt(currentMin);
                        let diff = (eH * 60 + eM) - (sH * 60 + sM);
                        if (diff < 0) diff += 24 * 60;
                        const durHours = Math.round(diff / 30) * 0.5;
                        if (durHours > 0 && durHours <= 12) updates.est_duration = durHours;
                        else updates.est_duration = '';
                      }
                      setForm(p => ({ ...p, ...updates }));
                    }}
                  >
                    <SelectTrigger className="w-20 font-mono"><SelectValue placeholder="HH" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-</SelectItem>
                      {HOURS_24.map(hr => <SelectItem key={hr} value={hr}>{hr}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <span className="text-muted-foreground font-mono">:</span>
                  <Select
                    value={form.end_time ? form.end_time.split(':')[1] : 'none'}
                    onValueChange={(min) => {
                      if (min === 'none' || !form.end_time) return;
                      const hr = form.end_time.split(':')[0];
                      setForm(p => ({ ...p, end_time: `${hr}:${min}` }));
                    }}
                  >
                    <SelectTrigger className="w-20 font-mono"><SelectValue placeholder="MM" /></SelectTrigger>
                    <SelectContent>
                      {MINUTES_5.map(min => <SelectItem key={min} value={min}>{min}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label className="label-overline">Description <span className="text-red-500">*</span></Label>
            <Textarea value={form.description} onChange={(e) => updateForm('description', e.target.value)} placeholder="Job description..." rows={2} />
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label className="label-overline">Notes</Label>
            <Textarea value={form.notes} onChange={(e) => updateForm('notes', e.target.value)} placeholder="Additional notes..." rows={2} />
          </div>

          {/* Tags */}
          <div className="space-y-2">
            <Label className="label-overline">Tags / Labels</Label>
            <div className="flex gap-2">
              <Input value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddTag(); } }} placeholder="Type to add tag..." list="pb-tag-suggestions" className="flex-1" />
              <datalist id="pb-tag-suggestions">{availableTags.filter(t => !form.tags.includes(typeof t === 'string' ? t : t?.name)).map((t, i) => <option key={`tag-opt-${typeof t === 'string' ? t : t?.name || i}`} value={typeof t === 'string' ? t : t?.name} />)}</datalist>
              <Button type="button" variant="outline" onClick={handleAddTag} disabled={!tagInput.trim()}><Plus className="h-4 w-4" /></Button>
            </div>
            {form.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {form.tags.map((tag, i) => (
                  <Badge key={`tag-${typeof tag === 'string' ? tag : tag?.name || i}`} variant="secondary" className="pl-2 pr-1 py-1">
                    {typeof tag === 'string' ? tag : tag?.name}
                    <button type="button" onClick={() => setForm(p => ({ ...p, tags: p.tags.filter((_, idx) => idx !== i) }))} className="ml-1 hover:bg-muted rounded-full p-0.5"><XCircle className="h-3 w-3" /></button>
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* Night Shift & TBC Toggles */}
          <div className="flex items-center gap-6 pt-1" data-testid="pb-job-flags">
            <div className="flex items-center gap-2">
              <Switch id="pb-night-shift" checked={form.is_night_shift} onCheckedChange={v => setForm(p => ({ ...p, is_night_shift: v }))} data-testid="pb-night-shift-toggle" />
              <Label htmlFor="pb-night-shift" className="flex items-center gap-1.5 cursor-pointer text-sm">
                <Moon className="h-3.5 w-3.5 text-indigo-400" /> Night Shift
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch id="pb-tbc" checked={form.is_tbc} onCheckedChange={v => setForm(p => ({ ...p, is_tbc: v }))} data-testid="pb-tbc-toggle" />
              <Label htmlFor="pb-tbc" className="cursor-pointer text-sm text-zinc-400">To Be Confirmed (TBC)</Label>
            </div>
          </div>

          {/* ── Recurring Job Toggle ────────────────────────── */}
          <div className="space-y-3 pt-1">
            <div className="flex items-center gap-2">
              <Switch
                id="pb-recurring"
                checked={isRecurring}
                onCheckedChange={(v) => { setIsRecurring(v); if (!v) setRecurrenceConfig(null); }}
                data-testid="pb-recurring-toggle"
              />
              <Label htmlFor="pb-recurring" className="flex items-center gap-1.5 cursor-pointer text-sm">
                <Repeat className="h-3.5 w-3.5 text-amber-400" /> Make Recurring
              </Label>
            </div>
            {isRecurring && (
              <RecurrenceConfig
                startDate={form.start_date}
                onConfigChange={setRecurrenceConfig}
                api={api}
                formatDateDisplay={(d) => {
                  try { return format(new Date(d + 'T00:00:00'), tenantSettings?.date_format === 'MM/DD/YYYY' ? 'MM/dd/yyyy' : 'dd/MM/yyyy'); }
                  catch { return d; }
                }}
              />
            )}
          </div>

          {/* File Attachments */}
          <div className="space-y-2">
            <Label className="label-overline">Attachments</Label>
            <div className="border-2 border-dashed rounded-lg p-4 text-center"
              onDragOver={(e) => { e.preventDefault(); }}
              onDrop={(e) => { e.preventDefault(); setPendingFiles(prev => [...prev, ...Array.from(e.dataTransfer.files).map(f => ({ file: f, visibleToOperators: pendingFilesVisible }))]); }}
            >
              <Upload className="h-5 w-5 mx-auto text-muted-foreground mb-1" />
              <p className="text-xs text-muted-foreground">Drag & drop or click to attach</p>
              <div className="flex items-center justify-center gap-3 mt-2">
                <input ref={pendingFileInputRef} type="file" multiple className="hidden" onChange={(e) => { setPendingFiles(prev => [...prev, ...Array.from(e.target.files).map(f => ({ file: f, visibleToOperators: pendingFilesVisible }))]); e.target.value = ''; }} />
                <Button type="button" variant="outline" size="sm" onClick={() => pendingFileInputRef.current?.click()} className="text-xs h-7"><Upload className="h-3 w-3 mr-1" /> Choose Files</Button>
                <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
                  <input type="checkbox" checked={pendingFilesVisible} onChange={(e) => setPendingFilesVisible(e.target.checked)} className="rounded" />
                  Visible to operators
                </label>
              </div>
            </div>
            {pendingFiles.length > 0 && (
              <div className="space-y-1">
                {pendingFiles.map((pf, idx) => (
                  <div key={`pf-${pf.file.name}-${idx}`} className="flex items-center gap-2 px-2 py-1.5 border rounded text-xs bg-muted/30">
                    <FileIcon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    <span className="flex-1 truncate">{pf.file.name}</span>
                    <span className="text-muted-foreground shrink-0">{pf.file.size < 1048576 ? `${(pf.file.size / 1024).toFixed(0)} KB` : `${(pf.file.size / 1048576).toFixed(1)} MB`}</span>
                    <button type="button" onClick={() => setPendingFiles(p => p.map((f, i) => i === idx ? { ...f, visibleToOperators: !f.visibleToOperators } : f))} className={pf.visibleToOperators ? 'text-green-500' : 'text-muted-foreground/40'}>
                      {pf.visibleToOperators ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                    </button>
                    <button type="button" onClick={() => setPendingFiles(p => p.filter((_, i) => i !== idx))} className="text-muted-foreground hover:text-destructive"><XCircle className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <SheetFooter className="pt-4 border-t mt-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button className="btn-volt" onClick={handleSubmit} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
            Create Job
          </Button>
        </SheetFooter>
      </SheetContent>

      {/* QUICK ADD CUSTOMER DIALOG */}
      <QuickAddCustomerDialog
        open={customerDialogOpen}
        onOpenChange={setCustomerDialogOpen}
        api={api}
        onCreated={handleCustomerCreated}
      />

      {/* QUICK ADD CONTACT DIALOG */}
      <Dialog open={contactDialogOpen} onOpenChange={setContactDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <User className="h-5 w-5 text-[#ffd108]" />
              Add Contact
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateContact} className="space-y-4">
            <div className="p-3 bg-muted rounded-sm">
              <p className="text-sm text-muted-foreground">
                Adding contact for: <span className="font-medium text-foreground">{customers.find(c => c.id === form.customer_id)?.company_name}</span>
              </p>
            </div>
            <div className="space-y-2">
              <Label className="label-overline">Name <span className="text-red-500">*</span></Label>
              <Input data-testid="pb-contact-name-input" value={contactForm.name} onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })} placeholder="John Smith" required />
            </div>
            <div className="space-y-2">
              <Label className="label-overline">Position / Title</Label>
              <Input data-testid="pb-contact-position-input" value={contactForm.position} onChange={(e) => setContactForm({ ...contactForm, position: e.target.value })} placeholder="Site Manager" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="label-overline">Phone</Label>
                <Input data-testid="pb-contact-phone-input" value={contactForm.phone} onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })} placeholder="0400 000 000" />
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Email</Label>
                <Input data-testid="pb-contact-email-input" type="email" value={contactForm.email} onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })} placeholder="john@example.com" />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setContactDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="btn-volt" data-testid="pb-contact-submit-btn"><Plus className="h-4 w-4 mr-2" />Add Contact</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* QUICK ADD SITE DIALOG */}
      <Dialog open={siteDialogOpen} onOpenChange={setSiteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <MapPin className="h-5 w-5 text-[#ffd108]" />
              Quick Add Site
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateSite} className="space-y-4">
            <div className="p-3 bg-muted rounded-sm">
              <p className="text-sm text-muted-foreground">
                Adding site for: <span className="font-medium text-foreground">{customers.find(c => c.id === form.customer_id)?.company_name}</span>
              </p>
            </div>
            <div className="space-y-3 p-3 border rounded-lg bg-muted/30">
              <div>
                <Label className="text-xs text-muted-foreground">Search Address</Label>
                <GooglePlacesAutocomplete
                  placeholder="Start typing an address..."
                  country={tenantSettings?.country}
                  onPlaceSelect={(place) => {
                    setSiteForm(prev => ({
                      ...prev,
                      name: [place.street_address, place.suburb].filter(Boolean).join(', ') || place.name || prev.name,
                      street_address: place.street_address || '',
                      suburb: place.suburb || '',
                      state: place.state || '',
                      postcode: place.postcode || ''
                    }));
                  }}
                  data-testid="pb-site-address-autocomplete"
                />
                <p className="text-xs text-muted-foreground mt-1">Or enter manually below</p>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Site Name</Label>
                <Input value={siteForm.name} onChange={(e) => setSiteForm({ ...siteForm, name: e.target.value })} placeholder="e.g. 17 Highview Cres, Abuntha" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Street Address <span className="text-red-500">*</span></Label>
                <Input value={siteForm.street_address} onChange={(e) => setSiteForm({ ...siteForm, street_address: e.target.value })} placeholder="e.g., 123 Main Street" required />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-xs text-muted-foreground">{t('suburb')} <span className="text-red-500">*</span></Label>
                  <Input value={siteForm.suburb} onChange={(e) => setSiteForm({ ...siteForm, suburb: e.target.value })} placeholder={t('suburb')} required />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">{countryMeta?.stateLabel || t('state')}</Label>
                  <Select value={siteForm.state} onValueChange={(v) => setSiteForm({ ...siteForm, state: v })}>
                    <SelectTrigger>
                      <SelectValue placeholder={countryMeta?.stateLabel || t('state')} />
                    </SelectTrigger>
                    <SelectContent>
                      {statesList.map(s => (
                        <SelectItem key={s.code} value={s.code}>{s.code}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">{countryMeta?.postalLabel || t('postcode')}</Label>
                  <Input value={siteForm.postcode} onChange={(e) => setSiteForm({ ...siteForm, postcode: e.target.value })} placeholder="0000" maxLength={4} />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setSiteDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="btn-volt"><Plus className="h-4 w-4 mr-2" />Add Site</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Sheet>
  );
}
